// Shared helpers for the Admin stats page (desktop + mobile) - date-range
// computation and date-key counting/grouping used for the New Users / Active
// Users bar charts.

const pad2 = (n) => String(n).padStart(2, '0');

// ddMMyyyy, matching the date format the backend already uses elsewhere for
// dateAndTime fields (e.g. FIR.uploadedOn is "ddMMyyyy HH:mm").
export const formatDDMMYYYY = (date) =>
  `${pad2(date.getDate())}${pad2(date.getMonth() + 1)}${date.getFullYear()}`;

// dd-MM, used as the short bar-chart x-axis label.
export const formatShortLabel = (ddMMyyyy) =>
  `${ddMMyyyy.substring(0, 2)}-${ddMMyyyy.substring(2, 4)}`;

// Builds the last `days` dates (inclusive of today), oldest first, as
// { key: ddMMyyyy, label: dd-MM } - used to seed the chart with zero-count
// days so the X-axis always spans the full window even on quiet days.
export const getLastNDays = (days) => {
  const result = [];
  const today = new Date();

  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = formatDDMMYYYY(d);
    result.push({ key, label: formatShortLabel(key) });
  }

  return result;
};

// Extracts the ddMMyyyy date portion from a "ddMMyyyy HH:mm" dateAndTime string.
export const extractDateKey = (dateAndTime) => (dateAndTime || '').substring(0, 8);

// Counts every item by date (used for New Users - one signup per item).
export const countByDate = (list, days) => {
  const buckets = getLastNDays(days);
  const counts = {};
  buckets.forEach((b) => { counts[b.key] = 0; });

  (list || []).forEach((item) => {
    const key = extractDateKey(item.dateAndTime);
    if (key in counts) counts[key] += 1;
  });

  return buckets.map((b) => ({ ...b, value: counts[b.key] }));
};

// Counts unique mobiles per date (used for Active Users - a user may log in
// multiple times a day but should only count once per day on the chart).
export const countUniqueMobileByDate = (list, days) => {
  const buckets = getLastNDays(days);
  const setsByDate = {};
  buckets.forEach((b) => { setsByDate[b.key] = new Set(); });

  (list || []).forEach((item) => {
    const key = extractDateKey(item.dateAndTime);
    if (key in setsByDate && item.mobile) setsByDate[key].add(item.mobile);
  });

  return buckets.map((b) => ({ ...b, value: setsByDate[b.key].size }));
};

// Unique, sorted mobile numbers across the whole list - used both for the
// "Active Users" total (a user active on several days should count once) and
// for the downloadable list files.
export const uniqueMobiles = (list) => {
  const set = new Set();
  (list || []).forEach((item) => { if (item.mobile) set.add(item.mobile); });
  return Array.from(set).sort();
};
